import uuid from '@vend/vend-uuid'
import { VdFeatureManager } from '@vend/vend.js'
import angular, { IHttpResponse } from 'angular'
import jitterTime from 'jitter-time'

import { getFromStorage, IncCounter } from '@vend/utilities'
import { SERVICE_WORKER_EVENTS } from '../components/service-worker/service-worker.constant'
import { WEB_REGISTER } from '../components/web-register.constant'
import {
  EVENTS,
  InsufficientStateForOfflineError,
  POS_STATUS_URL,
  SALES_STATUS,
  STATUS,
} from '../constants'
import {
  $http,
  $interval,
  $log,
  $q,
  $rootScope,
  $window,
} from '../ng-compat/imports'
import { EventEmitter } from '../util/event-emitter'
import { PosStateManager } from './pos-state-manager'

const NS = '[WebRegisterStatusManager]'
const STATUS_QUERY_TIMEOUT = 120 * 1000
const STATUS_POLL_INTERVAL = jitterTime(2)

export interface RestrictedAccessChangeEvent {
  restrictedAccess: boolean
}

interface StatusChangeEvent {
  previousStatus: string
  status: string
}

type Events = {
  [EVENTS.insufficientStateForOffline]: []
  [EVENTS.restrictedAccessChange]: [RestrictedAccessChangeEvent]
  [EVENTS.softRefreshRequired]: []
  [EVENTS.statusChange]: [StatusChangeEvent]
  [EVENTS.transitionOffline]: []
  [EVENTS.transitionOnline]: []
}

/**
 * Manages whether to trigger offline mode, any jobs or migrations to run, etc.
 *
 * @class WebRegisterStatusManager
 * @extends {EventEmitter}
 * @constructor
 */
export class WebRegisterStatusManager extends EventEmitter<Events> {
  /**
   * The unique ID of this client. Helps identify which clients have triggered hard/soft refreshes so we don't
   * end up in an infinite loop of refreshing.
   */
  private _clientId: string | null | undefined = null

  /**
   * The current (online) status of the application
   */
  private _currentStatus = STATUS.ONLINE

  private _featuresInitialised: $TSFixMe
  private _reloadingApplication: $TSFixMe

  /**
   * Identify that the register is in a restricted state.
   */
  private _restrictedAccess = false

  /**
   * Whether a soft refresh has been triggered.
   */
  private _softRefreshRequired = false

  private _whenInitialised: $TSFixMe

  constructor(
    private posStateManager: PosStateManager,
    private vdFeatureManager: VdFeatureManager,
    wrDebugTools: $TSFixMe,
    private serviceWorkerManager: $TSFixMe
  ) {
    super()
    wrDebugTools.register('webRegisterStatusManager')
  }

  /**
   * Current status of web register. See #STATUS for the list of possible values.
   *
   * @readOnly
   * @property status
   * @type {String}
   */
  public get status() {
    return this._currentStatus
  }

  /**
   * Whether a soft refresh has been triggered.
   *
   * @readOnly
   * @property softRefreshRequired
   * @type {Boolean}
   */
  public get softRefreshRequired() {
    return this._softRefreshRequired
  }

  /**
   * Identify that the register is in a restricted state.
   *
   * @property restrictedAccess
   * @type {Boolean}
   */
  public get restrictedAccess() {
    return this._restrictedAccess
  }

  public set restrictedAccess(isRestricted) {
    this._restrictedAccess = isRestricted
    this._notify(EVENTS.restrictedAccessChange, {
      restrictedAccess: isRestricted,
    })
  }

  // ============================== Public API ============================== //

  /**
   * Initialise the status manager, querying the initial status.
   *
   * @method init
   *
   * @return {Promise<this>} A promise resolved with the manager instance once initialisation is complete
   */
  public init() {
    if (!this._whenInitialised) {
      this._featuresInitialised = this.vdFeatureManager.init()

      this._whenInitialised = this.posStateManager
        .init()
        .then(() => {
          this._clientId = this.posStateManager.getClientId()

          if (!this._clientId) {
            this._clientId = uuid()
            this.posStateManager.setClientId(this._clientId)
          }
        })
        .then(() => {
          if (this._isSufficientStateForOffline()) {
            this._setStatus(STATUS.ONLINE)
            this._checkPosStatus()
            return this
          } else {
            return this._checkPosStatus().then(() => {
              if (!this.isOnline()) {
                return $q.reject(new InsufficientStateForOfflineError())
              }

              return this
            })
          }
        })
        // Initialise the status manager regardless of whether init fails so we still start the regular poll etc and
        // should we remain on the page will still update the online status should that change.
        .finally(() => this._init())
    }

    return this._whenInitialised
  }

  /**
   * Check whether the application is currently online.
   *
   * @method isOnline
   *
   * @return {Boolean} true if the application is online, false otherwise
   */
  public isOnline() {
    return this._currentStatus === STATUS.ONLINE
  }

  /**
   * Wait for the application to be online.
   *
   * @method whenOnline
   *
   * @return {Promise} A promise resolved as soon as the application is online
   */
  public whenOnline() {
    return $q(resolve => {
      if (this.isOnline()) {
        resolve()
      } else {
        this.once(EVENTS.transitionOnline, resolve)
      }
    })
  }

  /**
   * Refresh the application cache and reload the page with the latest version of the application.
   *
   * @method refreshCacheAndReload
   *
   * @param  options {Object}
   */
  public refreshCacheAndReload() {
    if (this.serviceWorkerManager.activeServiceWorker) {
      return this.serviceWorkerManager.refreshServiceWorkerCache()
    }
  }

  /**
   * Refresh the POS status by querying the status from the /pos/status.json API.
   *
   * @method refreshPosStatus
   *
   * @return {Promise<Response>} A promise resolved with the response from querying the server
   */
  public refreshPosStatus() {
    return this._checkPosStatus()
  }

  // ============================== Private API ============================== //

  /**
   * Initialise the status manager. Starts the regular status poll and binds internal events.
   *
   * @private
   * @method _init
   */
  private _init() {
    $interval(() => this._checkPosStatus(), STATUS_POLL_INTERVAL)

    this.on(
      EVENTS.transitionOffline,
      this._ensureSufficientStateForOffline,
      this
    )

    this.serviceWorkerManager.on(
      SERVICE_WORKER_EVENTS.SERVICE_WORKER_INSTALLED,
      this._showSoftRefreshBanner,
      this
    )
    this.serviceWorkerManager.on(
      SERVICE_WORKER_EVENTS.SERVICE_WORKER_UPDATED,
      this._reloadApplication,
      this
    )

    return $q.all([
      this._featuresInitialised,
      this.serviceWorkerManager.registerServiceWorker(),
    ])
  }

  /**
   * Query the status from the /pos/status.json API.
   *
   * @private
   * @method _checkPosStatus
   *
   * @return {Promise<Response>} A promise resolved with the response from querying the server
   */
  private _checkPosStatus() {
    const config = {
      params: {
        id: this._clientId,
        version: WEB_REGISTER.CLIENT_VERSION,
        application: WEB_REGISTER.APPLICATION_NAME,
      } as $TSFixMe,
      timeout: STATUS_QUERY_TIMEOUT,
    }

    const errorSalesCount = getFromStorage(SALES_STATUS.VEND_ERROR_SALES_COUNT)

    if (angular.isDefined(errorSalesCount)) {
      config.params.errored_sales_count = errorSalesCount
    }
    return $http
      .get<$TSFixMe>(POS_STATUS_URL, config)
      .then(response => this._processStatusSuccessResponse(response))
      .catch(response => this._processStatusErrorResponse(response))
  }

  /**
   * Process a successful response from /pos/status.json.
   *
   * @private
   * @method _processStatusSuccessResponse
   *
   * @param  response {Response}
   *         The $http response object
   *
   * @return {Response} The $http response object
   */
  private _processStatusSuccessResponse(response: IHttpResponse<$TSFixMe>) {
    const posStatus = response.data

    if (!angular.isObject(posStatus)) {
      // Response was not JSON - could have been an error page - set status to unreachable
      this._setStatus(STATUS.UNREACHABLE)
      return response
    }

    if (posStatus.sell_screen_online) {
      this._setStatus(STATUS.ONLINE)
    } else {
      this._setStatus(STATUS.REMOTE_DISABLED)
    }

    if (posStatus.has_command) {
      $log.debug(`${NS} Remote command received`, posStatus)

      switch (posStatus.command) {
        case 'refresh_hard':
          this.refreshCacheAndReload()
          break

        case 'refresh_soft':
          this._showSoftRefreshBanner()
          break
      }
    }

    return response
  }

  /**
   * Process an error response from /pos/status.json.
   *
   * @method _processStatusErrorResponse
   *
   * @param  response {Response}
   *         The $http response object
   *
   * @return {Response} The $http response object
   */
  private _processStatusErrorResponse(response: Response) {
    const status = response.status

    if (status === 401) {
      this._setStatus(STATUS.UNAUTHENTICATED)
    } else {
      this._setStatus(STATUS.UNREACHABLE)
    }

    return response
  }

  /**
   * Set the current status of the web register client to the given status.
   *
   * @private
   * @method _setStatus
   *
   * @param  {String} status
   *         The status (any one of STATUS)
   */
  private _setStatus(status: STATUS) {
    if (status === this._currentStatus) {
      return
    }
    if (!STATUS[status]) {
      $log.warn(`${NS} Attempt to set unrecognized status: `, status)
      return
    }

    const previousStatus = this._currentStatus
    this._currentStatus = status

    this._notifyStatusChange(previousStatus)

    // Transitions to OFFLINE, will fail to be posted, but we'll be able to
    // see when it goes back online.
    IncCounter('webregister.status_change_counter', 1, {
      from: previousStatus,
      to: status,
    })
  }

  /**
   * Determine whether the application has sufficient state to operate offline (at a bare minimum we require a
   * logged in user and at least one complete sync to have been performed).
   *
   * @private
   * @method _isSufficientStateForOffline
   *
   * @return {Boolean} True if there is sufficient state to operate offline, false otherwise
   */
  private _isSufficientStateForOffline() {
    return Boolean(
      this.posStateManager.getUser() &&
        this.posStateManager.getInitialSyncComplete()
    )
  }

  /**
   * Ensure there is sufficient state to operate offline, emit an event if that is not the case.
   *
   * @private
   * @method _ensureSufficientStateForOffline
   */
  private _ensureSufficientStateForOffline() {
    if (!this._isSufficientStateForOffline()) {
      $rootScope.$emit(EVENTS.insufficientStateForOffline)
    }
  }

  /**
   * Emit the relevant events for a change in status.
   *
   * @private
   * @method _notifyStatusChange
   *
   * @param  previousStatus {String}
   *         The previous status prior to the status change
   */
  private _notifyStatusChange(previousStatus: string) {
    const status = this._currentStatus

    const eventData = { status, previousStatus }

    this._notify(EVENTS.statusChange, eventData)

    if (this.isOnline()) {
      this._notify(EVENTS.transitionOnline)
    } else if (previousStatus === STATUS.ONLINE) {
      this._notify(EVENTS.transitionOffline)
    }
  }

  /**
   * Emit an event on the manager instance and the $rootScope.
   *
   * @private
   * @method _notify
   *
   * @param  event {String}
   *         The event to emit
   *
   * @param  ...args {...Any}
   *         Additional information to emit with the event
   */
  private _notify<E extends keyof Events>(event: E, ...args: Events[E]) {
    this.emit(event, ...args)
  }

  private _showSoftRefreshBanner() {
    this._softRefreshRequired = true
    this._notify(EVENTS.softRefreshRequired)
  }

  /**
   * Reloads the application (ignoring the browser cache) to get the latest version.
   *
   * @private
   * @method _reloadApplication
   */
  private _reloadApplication() {
    if (this._reloadingApplication) {
      return
    }

    $window.location.reload()
    this._reloadingApplication = true
  }
}

angular
  .module('webregister.managers')
  .service('webRegisterStatusManager', WebRegisterStatusManager)
